import { Button } from "@/components/ui/button";
import { X, Pencil, Trash2 } from "lucide-react";
import { MAXI_CONTAINER_TYPES } from "./mapConstants";

export default function MaxicontainerDetailDialog({
  selectedMaxicontainer,
  onClose,
  MAXICONTAINER_STATUSES,
  updateMaxicontainerStatus,
  deleteMaxicontainer,
  canPerformAction,
  onEdit,
}) {
  if (!selectedMaxicontainer) return null;

  return (
    <div 
      className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-zinc-900 border border-zinc-700 rounded-t-xl sm:rounded-xl shadow-2xl w-full sm:max-w-md max-h-[85vh] sm:max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-4 h-4 rounded-full" 
              style={{ backgroundColor: MAXICONTAINER_STATUSES[selectedMaxicontainer.status]?.color || '#22c55e' }}
            />
            <h2 className="text-lg font-bold text-white">{selectedMaxicontainer.name}</h2>
            {selectedMaxicontainer.qr_ref && (
              <span className="text-xs font-mono font-bold text-primary bg-primary/10 border border-primary/30 rounded px-2 py-0.5" data-testid="maxi-qr-ref">
                {selectedMaxicontainer.qr_ref}
              </span>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Info */}
        <div className="p-4">
          <div className="mb-4 space-y-2">
            <p className="text-sm text-zinc-400">
              Container-type: <span className="text-white font-medium">{selectedMaxicontainer.container_type || 'Ikke angivet'}</span>
            </p>
            <p className="text-sm text-zinc-400">
              Fraktion: <span className="text-white font-medium">{selectedMaxicontainer.fraction || 'Ikke angivet'}</span>
            </p>
            <p className="text-sm text-zinc-400">
              Status: <span className="text-white font-medium">{MAXICONTAINER_STATUSES[selectedMaxicontainer.status]?.label}</span>
            </p>
            {selectedMaxicontainer.description && (
              <div className="mt-3 p-3 bg-zinc-800/60 border border-zinc-700 rounded-md" data-testid="maxi-description">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1 font-mono">Beskrivelse</p>
                <p className="text-sm text-zinc-100 whitespace-pre-wrap break-words">{selectedMaxicontainer.description}</p>
              </div>
            )}
          </div>
          
          {/* DRIFT TOGGLE */}
          <div className="mb-4 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Drift-status</p>
            <div className="flex gap-2">
              <Button 
                onClick={() => updateMaxicontainerStatus(selectedMaxicontainer.id, 'empty')}
                className={`flex-1 ${
                  selectedMaxicontainer.status !== 'out_of_service'
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-zinc-700 hover:bg-green-700'
                }`}
                data-testid="maxi-in-drift-btn"
              >
                I drift
              </Button>
              <Button 
                onClick={() => updateMaxicontainerStatus(selectedMaxicontainer.id, 'out_of_service')}
                className={`flex-1 ${
                  selectedMaxicontainer.status === 'out_of_service'
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-zinc-700 hover:bg-red-700'
                }`}
                data-testid="maxi-out-drift-btn"
              >
                Ude af drift
              </Button>
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              {selectedMaxicontainer.status === 'out_of_service' 
                ? 'Container er ikke i drift (ikke opsat/fjernet)'
                : 'Container er i drift og kan bruges'}
            </p>
          </div>
          
          {/* Status ændring knapper - tvunget flow */}
          {selectedMaxicontainer.status !== 'out_of_service' && (
          <div className="space-y-2">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Tømningsstatus</p>
            
            {(selectedMaxicontainer.status === 'empty' || selectedMaxicontainer.status === '75_pct') && (
              <>
                {selectedMaxicontainer.status !== '75_pct' && (
                  <Button 
                    onClick={() => updateMaxicontainerStatus(selectedMaxicontainer.id, '75_pct')}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    75% fuld
                  </Button>
                )}
                <Button 
                  onClick={() => updateMaxicontainerStatus(selectedMaxicontainer.id, 'pending')}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                >
                  Bestil tømning
                </Button>
              </>
            )}
            
            {selectedMaxicontainer.status === 'pending' && (
              <Button 
                onClick={() => updateMaxicontainerStatus(selectedMaxicontainer.id, 'in_progress')}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                Tømning på vej
              </Button>
            )}
            
            {selectedMaxicontainer.status === 'in_progress' && (
              <Button 
                onClick={() => updateMaxicontainerStatus(selectedMaxicontainer.id, 'empty')}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                Marker som tømt
              </Button>
            )}
            
            <p className="text-xs text-zinc-500 text-center mt-1">
              Flow: Bestil &rarr; På vej &rarr; Tømt
            </p>
          </div>
          )}
          
          {/* Rediger og Slet knapper - RBAC-beskyttet */}
          {(canPerformAction('ressource_rediger') || canPerformAction('ressource_slet')) && (
            <div className="mt-4 pt-4 border-t border-zinc-700 space-y-2">
              {canPerformAction('ressource_rediger') && (
              <Button 
                onClick={() => onEdit(selectedMaxicontainer)}
                variant="secondary"
                className="w-full"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Rediger maxicontainer
              </Button>
              )}
              {canPerformAction('ressource_slet') && (
              <Button 
                onClick={() => deleteMaxicontainer(selectedMaxicontainer.id, selectedMaxicontainer.name)}
                variant="destructive"
                className="w-full"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Slet maxicontainer
              </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
